"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

/** One-line demo prompts — type on, pause, delete, cycle (Chat-style idle animation). */
const DEMO_LINES = [
  "Build a site for my marketing agency.",
  "Launch a fitness coaching landing page that books calls.",
  "Create a portfolio for my photography work.",
  "Make a coffee shop site with menu, hours, and online orders.",
  "Set up a SaaS waitlist page with email capture.",
  "Build a small business site with services and a contact form.",
];

const TYPE_MS = 36;
const DELETE_MS = 18;
const PAUSE_FULL_MS = 2400;
const PAUSE_EMPTY_MS = 450;

export function HeroPromptTypewriterOverlay({
  active,
  className,
  lines,
  textClassName,
}: {
  active: boolean;
  className?: string;
  lines?: string[];
  textClassName?: string;
}) {
  const cycleLines = lines && lines.length > 0 ? lines : DEMO_LINES;
  const [text, setText] = useState("");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!active) {
      setText("");
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    let cancelled = false;
    let lineIdx = 0;
    let charIdx = 0;
    let mode: "type" | "delete" = "type";

    const schedule = (fn: () => void, ms: number) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        if (!cancelled) fn();
      }, ms);
    };

    const step = () => {
      if (cancelled) return;
      const line = cycleLines[lineIdx % cycleLines.length];

      if (mode === "type") {
        if (charIdx < line.length) {
          charIdx += 1;
          setText(line.slice(0, charIdx));
          schedule(step, TYPE_MS);
        } else {
          schedule(() => {
            mode = "delete";
            step();
          }, PAUSE_FULL_MS);
        }
      } else {
        if (charIdx > 0) {
          charIdx -= 1;
          setText(line.slice(0, charIdx));
          schedule(step, DELETE_MS);
        } else {
          lineIdx += 1;
          mode = "type";
          schedule(step, PAUSE_EMPTY_MS);
        }
      }
    };

    setText("");
    charIdx = 0;
    mode = "type";
    schedule(step, 500);

    return () => {
      cancelled = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [active]);

  if (!active) return null;

  return (
    <div
      className={cn(
        "pointer-events-none absolute inset-x-0 top-0 z-[1] overflow-hidden px-4 pb-2 pt-3.5 text-left sm:px-5 sm:pt-4",
        className,
      )}
      aria-hidden
    >
      <p
        className={cn(
          "line-clamp-3 min-h-[2.75rem] font-[family-name:var(--font-rubik),sans-serif] text-base font-normal leading-6 tracking-normal text-[#64748B] sm:min-h-12 dark:text-muted-foreground",
          textClassName,
        )}
      >
        {text}
        <span
          className="ml-0.5 inline-block h-[1.05em] w-px translate-y-[0.08em] animate-pulse bg-orange-500/70 align-middle dark:bg-orange-400/60"
          aria-hidden
        />
      </p>
    </div>
  );
}
